import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiSearch, FiX, FiBook, FiArrowRight } from 'react-icons/fi';
import { useAuthStore } from '../auth/state';
import './lib_search.css';

// Import chapter data to check enabled status
import { chapters as step1Chapters } from '../general/library/step1/library_step_1.js';
import { chapters as step2Chapters } from '../general/library/step2/library_step_2.js';

// Create mapping of enabled chapters
const getEnabledChapters = () => {
  const enabledChapters = {
    step1: new Set(),
    step2: new Set()
  };

  // Process Step 1 chapters
  step1Chapters.forEach(chapter => {
    if (chapter.enabled) {
      // Convert chapter title to match search data keys
      let chapterSlug;
      if (chapter.title === 'Hematology & Oncology') {
        chapterSlug = 'hematology_and_oncology';
      } else if (chapter.title === 'Musculoskeletal & Connective Tissue') {
        chapterSlug = 'musculoskeletal_and_connective_tissue';
      } else if (chapter.title === 'Ethics & Communication') {
        chapterSlug = 'ethics_and_communication';
      } else if (chapter.title === 'Biostatistics & Epidemiology') {
        chapterSlug = 'biostatistics_and_epidemiology';
      } else if (chapter.title === 'Public Health Sciences') {
        chapterSlug = 'public_health_sciences';
      } else if (chapter.title === 'General Pathology') {
        chapterSlug = 'general_pathology';
      } else if (chapter.title === 'Anatomy & Histology') {
        chapterSlug = 'anatomy_and_histology';
      } else if (chapter.title === 'Special Senses') {
        chapterSlug = 'special_senses';
      } else if (chapter.title === 'Rapid Review') {
        chapterSlug = 'rapid_review';
      } else {
        chapterSlug = chapter.title.toLowerCase().replace(/[\s&]+/g, '_');
      }
      enabledChapters.step1.add(chapterSlug);
    }
  });

  // Process Step 2 chapters
  step2Chapters.forEach(chapter => {
    if (chapter.enabled) {
      // Convert chapter title to match search data keys
      let chapterSlug = chapter.title
        .toLowerCase()
        .replace(/,/g, '') // Remove commas
        .replace(/_&_/g, '_and_') // Replace _&_ with _and_
        .replace(/&/g, 'and') // Replace remaining & with and
        .replace(/\s+/g, '_') // Replace spaces with underscore
        .replace(/_+/g, '_') // Replace multiple underscores with single one
        .replace(/^_|_$/g, ''); // Remove leading/trailing underscores

      // Special case mappings
      const slugMappings = {
        'respiratory_and_ent': 'respiratory_and_ent',
        'renal_and_urology': 'renal_and_urology',
        'hematology_and_oncology': 'hematology_and_oncology',
        'gastrointestinal': 'gastrointestinal',
        'gynecology_and_obstetrics': 'gynecology_and_obstetrics',
        'neonate_and_infant': 'neonate_and_infant',
        'biostatistics_and_epidemiology': 'biostatistics_and_epidemiology',
        'ethics_and_communication': 'ethics_and_communication',
        'healthcare_delivery': 'healthcare_delivery',
        'quality_and_safety': 'quality_and_safety',
        'pharmacology_review': 'pharmacology_review',
        'emergency_and_toxicology': 'emergency_and_toxicology',
        'the_well_patient': 'the_well_patient',
        'infectious_diseases': 'infectious_diseases',
        'surgery_and_anesthesia': 'surgery_and_anesthesia'
      };

      const finalSlug = slugMappings[chapterSlug] || chapterSlug;
      enabledChapters.step2.add(finalSlug);
    }
  });

  return enabledChapters;
};

let enabledChapters = null;

// Import all search data files
const importSearchData = async () => {
  const searchData = {};
  
  // Step 1 search files
  const step1Files = [
    'anatomy_and_histology', 'biochemistry', 'biostatistics_and_epidemiology',
    'cardiovascular', 'dermatology', 'endocrine', 'ethics_and_communication',
    'gastrointestinal', 'general_pathology', 'hematology_and_oncology',
    'immunology', 'microbiology', 'musculoskeletal_and_connective_tissue',
    'neurology', 'pharmacology', 'psychiatry', 'public_health_sciences',
    'rapid_review', 'renal', 'reproductive', 'respiratory', 'special_senses'
  ];

  // Step 2 search files
  const step2Files = [
    'biostatistics_and_epidemiology', 'cardiovascular', 'dermatology',
    'emergency_and_toxicology', 'endocrine', 'ethics_and_communication',
    'gastrointestinal', 'genetics', 'gynecology', 'healthcare_delivery',
    'hematology_and_oncology', 'immunology', 'infectious_diseases',
    'musculoskeletal', 'neonate_and_infant', 'neurology', 'obstetrics',
    'ophthalmology', 'pharmacology_review', 'psychiatry', 'quality_and_safety',
    'renal_and_urology', 'respiratory_and_ent', 'surgery_and_anesthesia',
    'the_well_patient'
  ];

  try {
    // Load Step 1 data
    for (const file of step1Files) {
      try {
        const module = await import(`./s1_search/search1_${file}.json`);
        if (module.default?.chapter) {
          searchData[`step1_${file}`] = module.default.chapter;
        }
      } catch (error) {
        console.warn(`Failed to load step1_${file}:`, error);
      }
    }

    // Load Step 2 data
    for (const file of step2Files) {
      try {
        const module = await import(`./s2_search/search2_${file}.json`);
        if (module.default?.chapter) {
          searchData[`step2_${file}`] = module.default.chapter;
        }
      } catch (error) {
        console.warn(`Failed to load step2_${file}:`, error);
      }
    }
  } catch (error) {
    console.error('Error loading search data:', error);
  }

  return searchData;
};

const LibrarySearch = ({ isOpen, onClose, isDarkMode }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchData, setSearchData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [stepFilter, setStepFilter] = useState('both'); // 'step1', 'step2', or 'both'
  const searchInputRef = useRef(null);
  const resultsRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useAuthStore();

  // Load search data on component mount
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      // Initialize enabled chapters on first load
      if (!enabledChapters) {
        enabledChapters = getEnabledChapters();
      }
      const data = await importSearchData();
      setSearchData(data);
      setIsLoading(false);
    };
    
    loadData();
  }, []);

  // Focus input when search opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 100);
    }
  }, [isOpen]);

  // Handle escape key and clicks outside
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
        setSearchQuery('');
        setSelectedIndex(-1);
      }
    };

    const handleClickOutside = (event) => {
      const searchContainer = document.querySelector('.lib-search-container');
      if (isOpen && searchContainer && !searchContainer.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Fuzzy search function with typo tolerance
  const fuzzyMatch = (text, query) => {
    if (!text || !query) return 0;
    
    text = text.toLowerCase();
    query = query.toLowerCase();
    
    // Exact match gets highest score
    if (text.includes(query)) {
      const exactMatchScore = query.length / text.length;
      return exactMatchScore * 100;
    }
    
    // Calculate Levenshtein distance for fuzzy matching
    const matrix = [];
    const textLen = text.length;
    const queryLen = query.length;
    
    for (let i = 0; i <= textLen; i++) {
      matrix[i] = [i];
    }
    
    for (let j = 0; j <= queryLen; j++) {
      matrix[0][j] = j;
    }
    
    for (let i = 1; i <= textLen; i++) {
      for (let j = 1; j <= queryLen; j++) {
        if (text.charAt(i - 1) === query.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1, // substitution
            matrix[i][j - 1] + 1,     // insertion
            matrix[i - 1][j] + 1      // deletion
          );
        }
      }
    }
    
    const distance = matrix[textLen][queryLen];
    const maxLen = Math.max(textLen, queryLen);
    const similarity = (maxLen - distance) / maxLen;
    
    // Adjust scoring for partial matches
    const partialMatchBonus = text.split(' ').some(word => 
      word.toLowerCase().startsWith(query.toLowerCase())
    ) ? 0.3 : 0;
    
    return (similarity + partialMatchBonus) * 80;
  };

  // Search through all loaded data
  const performSearch = useCallback((query) => {
    if (!query.trim() || Object.keys(searchData).length === 0) {
      setSearchResults([]);
      return;
    }

    const results = [];
    
    // Check if query contains space-separated chapter and topic (e.g., "cardiology anatomy")
    const queryParts = query.trim().split(/\s+/);
    const isChapterTopicQuery = queryParts.length >= 2;
    
    Object.entries(searchData).forEach(([key, chapter]) => {
      if (!chapter || !chapter.topics) return;
      
      const step = key.startsWith('step1_') ? 'step1' : 'step2';
      
      // Apply step filter
      if (stepFilter !== 'both' && step !== stepFilter) {
        return; // Skip this step if it doesn't match the filter
      }
      
      const chapterName = chapter.displayName || chapter.name;
      
      // Check if this chapter is enabled
      const chapterSlug = chapter.id;
      if (!enabledChapters[step].has(chapterSlug)) {
        return; // Skip disabled chapters
      }
      
      // Check if query matches chapter name closely
      const chapterMatchScore = fuzzyMatch(chapterName, query);
      const isChapterMatch = chapterMatchScore > 40; // Reduced threshold for chapter match
      
      // For chapter-topic queries, check if first part matches chapter
      let isChapterTopicMatch = false;
      let topicSearchTerm = query;
      if (isChapterTopicQuery) {
        const firstPartChapterScore = fuzzyMatch(chapterName, queryParts[0]);
        if (firstPartChapterScore > 35) { // Reduced threshold for chapter-topic match
          isChapterTopicMatch = true;
          topicSearchTerm = queryParts.slice(1).join(' '); // Use remaining parts for topic search
        }
      }
      
      // Search through topics
      chapter.topics.forEach(topic => {
        let score = 0;
        const searchableText = [
          topic.displayName || topic.name,
          topic.chapterId,
          chapterName,
          ...(topic.keywords || [])
        ].join(' ');
        
        // Calculate relevance score based on the appropriate search term
        if (isChapterTopicMatch) {
          // For chapter-topic queries, primarily match against topic name
          score = fuzzyMatch(topic.displayName || topic.name, topicSearchTerm);
          score += fuzzyMatch(searchableText, query) * 0.3; // Add some general relevance
        } else {
          // For regular queries, use full query
          score = fuzzyMatch(searchableText, query);
        }
        
        // Boost score for exact matches in topic title
        const topicQuery = isChapterTopicMatch ? topicSearchTerm : query;
        if ((topic.displayName || topic.name).toLowerCase().includes(topicQuery.toLowerCase())) {
          score += 25;
        }
        
        // Boost score for matches in chapter name
        if (chapterName.toLowerCase().includes(query.toLowerCase())) {
          score += 15;
        }
        
        // Major boost if this is a chapter-based search
        if (isChapterMatch || isChapterTopicMatch) {
          score += 40; // Significant boost for chapter matches
        }
        
        if (score > 15) { // Reduced minimum threshold for relevance
          results.push({
            id: `${step}_${chapter.id}_${topic.id}`,
            title: topic.displayName || topic.name,
            chapter: chapterName,
            step: step.toUpperCase(),
            url: topic.url,
            score,
            chapterId: chapter.id,
            topicId: topic.id,
            isChapterBasedResult: isChapterMatch || isChapterTopicMatch // Flag to indicate this came from chapter search
          });
        }
      });
    });

    // Apply route-based priority boost
    const currentPath = location.pathname;
    const isStep1Route = currentPath.includes('step1-library') || currentPath.includes('/step1');
    const isStep2Route = currentPath.includes('step2-library') || currentPath.includes('/step2');
    
    results.forEach(result => {
      if (isStep1Route && result.step === 'STEP1') {
        result.score += 50; // Boost Step 1 results when on Step 1 route
      } else if (isStep2Route && result.step === 'STEP2') {
        result.score += 50; // Boost Step 2 results when on Step 2 route
      }
    });

    // Sort by relevance score
    results.sort((a, b) => b.score - a.score);
    
    // If we have chapter-based results, prioritize them and limit per chapter
    if (results.some(r => r.isChapterBasedResult)) {
      const chapterResults = results.filter(r => r.isChapterBasedResult);
      const nonChapterResults = results.filter(r => !r.isChapterBasedResult);
      
      // Group chapter results by step + chapter and show all results (no limit per step per chapter)
      const chapterGroups = {};
      chapterResults.forEach(result => {
        const groupKey = `${result.step}_${result.chapterId}`; // Use step + chapterId as key
        if (!chapterGroups[groupKey]) {
          chapterGroups[groupKey] = [];
        }
        chapterGroups[groupKey].push(result); // No limit - show all results
      });
      
      // Flatten grouped results and combine with non-chapter results
      const groupedChapterResults = Object.values(chapterGroups).flat();
      const finalResults = [...groupedChapterResults, ...nonChapterResults];
      
      // Show all results for chapter-based searches (no limit)
      setSearchResults(finalResults);
    } else {
      // Limit results to top 8 for regular searches
      setSearchResults(results.slice(0, 8));
    }
    
    setSelectedIndex(-1);
  }, [searchData, stepFilter]);

  // Handle search input changes
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    performSearch(query);
  };

  // Rerun search when step filter changes
  useEffect(() => {
    if (searchQuery.trim()) {
      performSearch(searchQuery);
    }
  }, [stepFilter, performSearch, searchQuery]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (searchResults.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < searchResults.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : searchResults.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && searchResults[selectedIndex]) {
          handleResultClick(searchResults[selectedIndex]);
        }
        break;
    }
  };

  // Handle result selection
  const handleResultClick = (result) => {
    const basePath = isAdmin ? '/admin' : '';
    const stepPath = result.step.toLowerCase();
    const url = `${basePath}/library/${stepPath}/${result.chapterId}/topic/${result.topicId}`;
    
    navigate(url);
    onClose();
    setSearchQuery('');
    setSelectedIndex(-1);
  };

  // Clear search
  const handleClear = () => {
    setSearchQuery('');
    setSearchResults([]);
    setSelectedIndex(-1);
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  if (!isOpen) return null;

  return (
    <div className={`lib-search-overlay ${isDarkMode ? 'dark' : ''}`}>
      <div className="lib-search-container">
        {/* Search Header */}
        <div className="lib-search-header">
          <div className="lib-search-input-container">
            <FiSearch className="lib-search-input-icon" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyDown={handleKeyDown}
              placeholder="Search topics, chapters, or keywords..."
              className="lib-search-input"
              autoComplete="off"
            />
            {searchQuery && (
              <button
                onClick={handleClear}
                className="lib-search-clear-btn"
                aria-label="Clear search"
              >
                <FiX />
              </button>
            )}
          </div>
          <div className="lib-search-header-controls">
            <span className="lib-search-beta-badge">Beta</span>
            <button
              onClick={onClose}
              className="lib-search-close-btn"
              aria-label="Close search"
            >
              <FiX />
            </button>
          </div>
        </div>

        {/* Step Filter */}
        <div className="lib-search-step-filter">
          <div className="lib-search-step-filter-buttons">
            <button
              className={`lib-search-step-btn ${stepFilter === 'both' ? 'active' : ''}`}
              onClick={() => setStepFilter('both')}
            >
              All Steps
            </button>
            <button
              className={`lib-search-step-btn ${stepFilter === 'step1' ? 'active' : ''}`}
              onClick={() => setStepFilter('step1')}
            >
              Step 1
            </button>
            <button
              className={`lib-search-step-btn ${stepFilter === 'step2' ? 'active' : ''}`}
              onClick={() => setStepFilter('step2')}
            >
              Step 2
            </button>
          </div>
        </div>

        {/* Search Results */}
        <div className="lib-search-results" ref={resultsRef}>
          {isLoading ? (
            <div className="lib-search-loading">
              <div className="lib-search-spinner"></div>
              <span>Loading search data...</span>
            </div>
          ) : searchQuery.trim() === '' ? (
            <div className="lib-search-placeholder">
              <FiSearch className="lib-search-placeholder-icon" />
              <h3>Search the Library</h3>
              <p>Search through thousands of medical topics across USMLE Step 1 and Step 2.</p>
              <div className="lib-search-tips">
                <p><strong>Pro tips:</strong></p>
                <ul>
                  <li>Type a chapter name like "cardiology" to see all topics from that chapter</li>
                  <li>Use "chapter topic" format like "cardiology anatomy" for specific topics</li>
                  <li>Use keywords like "cardiology", "pharmacology", or "pathology"</li>
                  <li>Search for specific conditions like "myocardial infarction"</li>
                  <li>Use abbreviations like "MI", "CHF", or "COPD"</li>
                  <li>Our search is still in development, so please be patient!</li>
                </ul>
              </div>
            </div>
          ) : searchResults.length === 0 ? (
            <div className="lib-search-no-results">
              <FiBook className="lib-search-no-results-icon" />
              <h3>No results found</h3>
              <p>Try different keywords or check your spelling.</p>
              <div className="lib-search-suggestions">
                <p>Suggestions:</p>
                <ul>
                  <li>Use more general terms</li>
                  <li>Try synonyms or related terms</li>
                  <li>Search for the system (e.g., "cardiovascular")</li>
                </ul>
              </div>
            </div>
          ) : (
            <>
              <div className="lib-search-results-header">
                <span>{searchResults.length} result{searchResults.length !== 1 ? 's' : ''} found</span>
                {searchResults.some(r => r.isChapterBasedResult) && (
                  <span className="lib-search-chapter-match-indicator">
                    <FiBook style={{ marginRight: '4px' }} />
                    Chapter match found
                  </span>
                )}
              </div>
              <div className="lib-search-results-list">
                {searchResults.map((result, index) => (
                  <div
                    key={result.id}
                    className={`lib-search-result-item ${
                      index === selectedIndex ? 'selected' : ''
                    } ${result.isChapterBasedResult ? 'chapter-based-result' : ''}`}
                    onClick={() => handleResultClick(result)}
                    onMouseEnter={() => setSelectedIndex(index)}
                  >
                    <div className="lib-search-result-content">
                      <div className="lib-search-result-title">
                        {result.isChapterBasedResult && (
                          <FiBook className="lib-search-chapter-icon" />
                        )}
                        {result.title}
                      </div>
                      <div className="lib-search-result-meta">
                        <span className="lib-search-result-chapter">
                          {result.chapter}
                        </span>
                        <span className="lib-search-result-step">
                          {result.step}
                        </span>
                        {result.isChapterBasedResult && (
                          <span className="lib-search-chapter-badge">
                            Chapter Match
                          </span>
                        )}
                      </div>
                    </div>
                    <FiArrowRight className="lib-search-result-arrow" />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Search Footer */}
        {searchResults.length > 0 && (
          <div className="lib-search-footer">
            <div className="lib-search-navigation-hint">
              <span>Use ↑↓ arrows to navigate, Enter to select</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LibrarySearch;
